import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import {
  KakarikoButtonLarge,
  KakarikoDialog,
  KakarikoForm,
  KakarikoText,
  Validations,
} from 'library';
import { IClinicianFormData } from '../types';
import { flatStateReducer } from '../../../library/utils';

export interface IOwnProps {
  onCreateSuccess: () => Promise<void>;
  clinicId: string;
  clinicName: string;
}

export const ADD_CLINICIAN_FORM_LABELS = {
  firstName: 'Provider First Name',
  lastName: 'Provider Last Name',
  email: 'Email',
};

export const emptyFieldErrorMessage = (field: string): string =>
  `${field} must not be empty.`;
export const addClinicianSuccessMessage =
  'New clinician was added successfully';

export const initialFormData: IClinicianFormData = {
  firstName: '',
  lastName: '',
  email: '',
};

const AddClinician = (props: IOwnProps) => {
  const { clinicName, clinicId, onCreateSuccess } = props;
  const [formData, setFormData] = useReducer<
    React.Reducer<IClinicianFormData, IClinicianFormData>
  >(flatStateReducer, { ...initialFormData });

  const [errors, setErrors] = useReducer<
    React.Reducer<booleanGenericObject, booleanGenericObject>
  >(flatStateReducer, {});
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const clearForm = () => {
    setFormData({
      ...initialFormData,
    });
    setOpen(false);
  };

  const submitNewClinician = useCallback(async () => {
    if (!formData) return;
    setIsLoading(true);
    try {
      const clinician = { ...formData, locationId: clinicId };
      await WeaverApiClientInstance.createClinician(clinician);
      await onCreateSuccess();
      ErrorInterceptor.success(addClinicianSuccessMessage, true);
      clearForm();
    } catch (e) {
      let errors = e;
      if (e.error) {
        errors = e.errors.map((m: any) => ({ message: m.message }));
      }

      if (e.response) {
        errors = { message: e.response.data.message };
      }

      ErrorInterceptor.error({ errors });
    } finally {
      setIsLoading(false);
    }
  }, [formData, onCreateSuccess, clinicId]);

  const form = useMemo(
    () => (
      <KakarikoForm data={formData} setData={setFormData} setError={setErrors}>
        <KakarikoText
          errorMessage={emptyFieldErrorMessage('First Name')}
          label={ADD_CLINICIAN_FORM_LABELS.firstName}
          name="firstName"
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText
          name="lastName"
          errorMessage={emptyFieldErrorMessage('Last Name')}
          label={ADD_CLINICIAN_FORM_LABELS.lastName}
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText
          errorMessage={emptyFieldErrorMessage('Email')}
          label={ADD_CLINICIAN_FORM_LABELS.email}
          name="email"
          validation={[Validations.isValidEmail]}
        />
      </KakarikoForm>
    ),
    [formData],
  );

  return (
    <>
      <KakarikoButtonLarge
        buttonTheme="primary"
        disabled={!clinicName || !clinicId}
        handleClick={() => setOpen(true)}
      >
        Add Clinician
      </KakarikoButtonLarge>
      <KakarikoDialog
        isLoading={isLoading}
        errors={errors}
        handleCancel={() => clearForm()}
        handleClose={() => clearForm()}
        handleSubmit={submitNewClinician}
        open={open}
        title="Add Clinician"
      >
        {form}
      </KakarikoDialog>
    </>
  );
};

export default AddClinician;
